import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import articles from '@/data/articles';

const Writing = () => {
  useEffect(() => {
    document.title = 'Film Analysis | The Second Seat';
    window.scrollTo(0, 0);
  }, []);

  const scrollRef = useRef(null);

  const sorted = [...articles].sort((a, b) => {
    const months = { January: 0, February: 1, March: 2, April: 3, May: 4, June: 5, July: 6, August: 7, September: 8, October: 9, November: 10, December: 11 };
    const [dA, mA, yA] = a.date.split(' ');
    const [dB, mB, yB] = b.date.split(' ');
    const dateA = new Date(yA, months[mA], dA);
    const dateB = new Date(yB, months[mB], dB);
    return dateB - dateA;
  });

  return (
    <div className="h-screen overflow-hidden flex flex-col" style={{ background: '#000000' }}>
      {/* Header */}
      <nav className="flex-shrink-0 py-4 px-6 sm:px-8 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(240,235,224,0.06)' }}>
        <Link to="/" className="text-base sm:text-lg italic opacity-70 hover:opacity-100 transition-opacity" style={{ fontFamily: 'EB Garamond, serif', color: '#f0ebe0' }}>
          The Second Seat
        </Link>
        <div className="flex gap-4 sm:gap-6 items-center">
          <span className="font-mono text-[11px] sm:text-xs tracking-[0.15em] sm:tracking-[0.2em] uppercase opacity-75" style={{ color: '#f0ebe0' }}>
            Film Analysis
          </span>
          <Link to="/episodes" className="font-mono text-[11px] sm:text-xs tracking-[0.15em] uppercase opacity-75 hover:opacity-100 transition-opacity" style={{ color: '#e8c47a' }}>
            Episodes
          </Link>
        </div>
      </nav>

      {/* Subtitle */}
      <div className="flex-shrink-0 text-center py-6 sm:py-8 px-6">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="text-base sm:text-lg italic opacity-65"
          style={{ fontFamily: 'EB Garamond, serif', color: '#f0ebe0' }}
        >
          On how a film is made and what it says, going beyond box office narratives.
        </motion.p>
      </div>

      {/* Filmstrip */}
      <div className="flex-1 flex flex-col justify-center relative">
        {/* Perforation top */}
        <div className="flex-shrink-0 h-5 flex items-center gap-3 px-6 overflow-hidden">
          {Array.from({ length: 50 }).map((_, i) => (
            <div key={i} className="w-3 h-2.5 rounded-sm flex-shrink-0" style={{ background: 'rgba(240,235,224,0.07)' }} />
          ))}
        </div>

        {/* Horizontal scroll */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-x-auto overflow-y-hidden flex items-stretch gap-0 px-8 sm:px-12 py-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
        >
          <style>{`div::-webkit-scrollbar { display: none; }`}</style>
          {sorted.map((article, i) => (
            <motion.div
              key={article.slug}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="flex-shrink-0"
            >
              <Link
                to={`/writing/${article.slug}`}
                className="block w-56 sm:w-72 h-full mx-2 sm:mx-3 group relative"
                style={{ borderLeft: '1px solid rgba(240,235,224,0.06)', borderRight: '1px solid rgba(240,235,224,0.06)' }}
              >
                {/* Image frame */}
                <div className="h-36 sm:h-48 overflow-hidden relative" style={{ background: '#0a0a0a' }}>
                  {article.image && (
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover grayscale sepia brightness-[0.55] contrast-[1.15] group-hover:brightness-[0.75] group-hover:scale-[1.03] transition-all duration-500"
                    />
                  )}
                  {article.sharedBySubject && (
                    <div className="absolute top-2 left-2 font-mono text-[9px] tracking-[0.15em] uppercase px-2 py-1 rounded-sm" style={{ background: 'rgba(10,10,10,0.7)', color: '#e8c47a', border: '1px solid rgba(232,196,122,0.3)' }}>
                      From the Other Seat
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-4 sm:p-5 flex flex-col justify-between" style={{ minHeight: '150px' }}>
                  <div>
                    <h3 className="text-lg sm:text-lg leading-snug mb-2 group-hover:opacity-100 opacity-90 transition-opacity" style={{ fontFamily: 'EB Garamond, serif', color: '#f0ebe0' }}>
                      {article.title}
                    </h3>
                    <p className="text-sm sm:text-sm leading-relaxed opacity-65 line-clamp-2" style={{ fontFamily: 'EB Garamond, serif', color: '#f0ebe0' }}>
                      {article.desc}
                    </p>
                  </div>
                  <div className="mt-3 font-mono text-[10px] sm:text-[10px] tracking-[0.12em] uppercase opacity-75" style={{ color: '#f0ebe0' }}>
                    {article.publication}
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Perforation bottom */}
        <div className="flex-shrink-0 h-5 flex items-center gap-3 px-6 overflow-hidden">
          {Array.from({ length: 50 }).map((_, i) => (
            <div key={i} className="w-3 h-2.5 rounded-sm flex-shrink-0" style={{ background: 'rgba(240,235,224,0.07)' }} />
          ))}
        </div>
      </div>

      {/* Scroll hint */}
      <div className="flex-shrink-0 text-center py-4">
        <span className="font-mono text-[11px] sm:text-[10px] tracking-[0.15em] uppercase opacity-45 animate-pulse" style={{ color: '#f0ebe0' }}>
          Swipe to explore &rarr;
        </span>
      </div>
    </div>
  );
};

export default Writing;
